import Config from '../_config/config';
import type { AcademicDetailsResponse } from '@conceptwaves/shared-components';

// Use nodefork backend URL
const API_BASE_URL = Config.nodeSiteUrl;

// Helper function to get authentication headers
const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const currentUser = localStorage.getItem('currentUser');
  if (currentUser && currentUser !== 'undefined') {
    try {
      const user = JSON.parse(currentUser);
      const token = user.token;
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Failed to parse currentUser from localStorage:', error);
    }
  }

  return headers;
};

interface FetchAcademicDetailsParams {
  limit?: number;
  offset?: number;
  search?: string;
  courseId?: number;
  schoolCode?: string;
  yearIds?: number[];
  batchId?: number;
  enquiryId?: number;
  /**
   * Batch dossier / grade-card reports only. Folds a student's other
   * enrollments for the SAME course (dropout, withdrawal, passout, or a
   * parallel regular one) into their record, so one grade card carries the
   * full history. Leave off for dashboards, rankings and stat cards — those
   * report on the current enrollment alone.
   */
  includePriorEnrollments?: boolean;
}

export const fetchAcademicDetails = async (
  params: FetchAcademicDetailsParams = {}
): Promise<AcademicDetailsResponse> => {
  const queryParams = new URLSearchParams();

  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.offset) queryParams.append('offset', params.offset.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.courseId) queryParams.append('courseId', params.courseId.toString());
  if (params.schoolCode) queryParams.append('schoolCode', params.schoolCode);
  if (params.yearIds && params.yearIds.length > 0) queryParams.append('yearId', params.yearIds.join(','));
  if (params.batchId) queryParams.append('batchId', params.batchId.toString());
  if (params.enquiryId) queryParams.append('enquiryId', params.enquiryId.toString());
  if (params.includePriorEnrollments) queryParams.append('includePriorEnrollments', '1');

  const url = `${API_BASE_URL}v1/academic/details?${queryParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch academic details: ${response.statusText}`);
  }

  return response.json();
};

// Fetch all courses
export interface Course {
  courseId: number;
  courseName: string;
  courseCode: string;
  studentCount: number;
}

export interface CoursesResponse {
  success: boolean;
  data: Course[];
  total: number;
}

export const fetchCourses = async (yearIds?: number[]): Promise<CoursesResponse> => {
  const queryParams = new URLSearchParams();

  if (yearIds && yearIds.length > 0) {
    queryParams.append('yearId', yearIds.join(','));
  }

  const url = `${API_BASE_URL}v1/academic/courses${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch courses: ${response.statusText}`);
  }

  return response.json();
};

// Fetch all schools
export interface School {
  schoolId: number;
  schoolName: string;
  schoolCode: string;
  studentCount: number;
  regularStudentCount: number;
}

export interface SchoolsResponse {
  success: boolean;
  data: School[];
  total: number;
}

export const fetchSchools = async (yearIds?: number[]): Promise<SchoolsResponse> => {
  const queryParams = new URLSearchParams();

  if (yearIds && yearIds.length > 0) {
    queryParams.append('yearId', yearIds.join(','));
  }

  const url = `${API_BASE_URL}v1/academic/schools${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch schools: ${response.statusText}`);
  }

  return response.json();
};

// Fetch all academic years
export interface AcademicYear {
  yearId: number;
  yearName: string;
  studentCount: number;
  regularStudentCount: number;
}

export interface AcademicYearsResponse {
  success: boolean;
  data: AcademicYear[];
  total: number;
}

export const fetchAcademicYears = async (): Promise<AcademicYearsResponse> => {
  const url = `${API_BASE_URL}v1/academic/years`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch academic years: ${response.statusText}`);
  }

  return response.json();
};

// Fetch batches mapped to the logged-in instructor
export interface InstructorBatch {
  batchId: number;
  batchName: string;
  batchCode: string;
}

export interface InstructorBatchesResponse {
  success: boolean;
  data: InstructorBatch[];
  total: number;
}

export const fetchInstructorBatches = async (): Promise<InstructorBatchesResponse> => {
  const url = `${API_BASE_URL}v1/academic/instructor-batches`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch instructor batches: ${response.statusText}`);
  }

  return response.json();
};
